import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import LanguageSwitcher from './LanguageSwitcher';
import BuyerAccess from './BuyerAccess';
import { useBuyerAuth } from '../../contexts/BuyerAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useBuyerAuth();
  const { language, setLanguage } = useLanguage();

  const isSupplierDashboard = location?.pathname === '/supplier-dashboard';

  const navigationItems = [
    {
      path: '/home',
      label: { en: 'Home', zh: '首页' }
    },
    {
      path: '/browse-beans',
      label: { en: 'Browse Beans', zh: '浏览咖啡豆' }
    },
    {
      path: '/interactive-farm-map',
      label: { en: 'Farm Map', zh: '农场地图' }
    },
    {
      path: '/supplier-registration',
      label: { en: 'Become a Supplier', zh: '成为供应商' }
    },
    {
      path: '/suppliers',
      label: { en: 'Suppliers', zh: '供应商' }
    }
  ];

  const moreMenuItems = [
    {
      path: '/about-us',
      label: { en: 'About Us', zh: '关于我们' }
    },
    {
      path: '/contact',
      label: { en: 'Contact', zh: '联系我们' }
    }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const aboutDropdownItems = [
    {
      path: '/about-us',
      label: { en: 'About Us', zh: '关于我们' },
      icon: 'Info'
    },
    {
      path: '/contact',
      label: { en: 'Contact', zh: '联系我们' },
      icon: 'Mail'
    }
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] bg-card border-b border-border shadow-card">
        <div className="flex items-center justify-between h-[60px] px-5">
          <div className="flex items-center gap-8">
            <Link to="/home" className="flex items-center gap-2 no-underline" onClick={closeMobileMenu}>
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center transition-smooth hover:bg-primary/20">
                <Icon name="Coffee" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">Africoffee</span>
            </Link>

            {/* Navigation Links */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
                {language === 'en' ? 'Home' : '首页'}
              </Link>
              <Link to="/browse-beans" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
                {language === 'en' ? 'Browse Beans' : '浏览咖啡豆'}
              </Link>
              <Link to="/suppliers" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
                {language === 'en' ? 'Suppliers' : '供应商'}
              </Link>
              <Link to="/interactive-farm-map" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
                {language === 'en' ? 'Farm Map' : '农场地图'}
              </Link>
              <Link to="/supplier-registration" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
                {language === 'en' ? 'Become a Supplier' : '成为供应商'}
              </Link>
              
              {/* About Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setShowAboutDropdown(true)}
                onMouseLeave={() => setShowAboutDropdown(false)}
              >
                <button className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-smooth">
                  {language === 'en' ? 'About' : '关于'}
                  <Icon name="ChevronDown" size={16} className={`transition-transform ${showAboutDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showAboutDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg py-2 z-50">
                    {aboutDropdownItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-muted transition-smooth"
                      >
                        <Icon name={item?.icon} size={16} />
                        <span>{item?.label?.[language]}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <BuyerAccess language={language} />
            
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[200] lg:hidden">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={closeMobileMenu} />
          <nav className="absolute top-[60px] left-0 right-0 bg-card border-b border-border shadow-modal max-h-[calc(100vh-60px)] overflow-y-auto">
            <div className="p-4 space-y-1">
              {[...navigationItems, ...moreMenuItems]?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMobileMenu}
                  className={`block px-4 py-3 rounded-md text-base font-medium transition-smooth no-underline ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {item?.label?.[language]}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;